<template>
  <div
    v-if="!_isEmpty(WIDGET_DATA)"
    class="form-widget"
    :data-testid="dataTestid"
  >
    <div class="bg-semantic-inverse-surface px-5 pb-5 p-4 rounded-2.5xl">
      <!-- eslint-disable vue/no-v-html -->
      <p
        class="text-semantic-inverse-on-surface text-lg mb-2"
        :class="titleClass"
        v-html="VERTICAL_DATA.title"
      >
      </p>

      <form
        class="flex flex-wrap -mx-2"
        @submit.prevent="handleSubmit"
      >
        <div
          v-for="(item, itemIndex) in VERTICAL_DATA.formData"
          :key="itemIndex"
          class="px-2 mb-2 last:mb-0"
          :class="item.componentClass"
        >
          <component
            :is="getKey(item.component, componentMap)"
            v-bind="item.props"
            v-model="formModel[item.props.name]"
            :error-message="validation?.[item.props.name]?.errorMessage || ''"
            :is-success="validation?.[item.props.name]?.isSuccess || false"
            class="lg:text-base"
            @update="handleValidateForm(item.props.name)"
          />
        </div>

        <div class="px-2 w-full pt-2">
          <atoms-button
            type="submit"
            size="large"
            class="w-full justify-center border !border-semantic-inverse-primary hover:!border-semantic-inverse-primary-variant !bg-semantic-inverse-primary hover:!bg-semantic-inverse-primary-variant !text-semantic-inverse-on-primary"
          >
            GET A QUOTE
          </atoms-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import { VERTICAL } from '~/helpers/vertical.constants'

import {
  ERROR_MAP_OBJ,
  WIDGET_DATA_OBJ
} from '~/constants/funnel/vertical-widget'

import {
  PAGE_DATA
} from '~/constants/healthinsurance.constants'

import { validateAge } from '~/helpers/validator'

import {
  GENERATE_FORM_MODEL_TEST
} from '~/helpers/formfunnel.helpers'

const { geodataSource } = useEnv()
const $router = useRouter()

const incomeProtectionStore = useIncomeProtectionStore()
const healthInsuranceStore = useHealthInsuranceStore()
const lifeInsuranceStore = useLifeInsuranceStore()

defineOptions({
  name: 'V2MoleculesVerticalFormWidgetSidebar'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'vertical-form-widget-sidebar'
  },

  vertical: {
    type: String,
    default: VERTICAL.HEALTH_INSURANCE
  },

  isIncomeProtection: {
    type: Boolean,
    default: false
  },

  titleClass: {
    type: String,
    default: ''
  }
})

const tempVertical = (() => {
  if (
    props.vertical === 'Life Insurance' &&
    props.isIncomeProtection
  ) {
    return 'Income Protection'
  }

  return props.vertical
})()

const WIDGET_DATA = getKey(tempVertical, WIDGET_DATA_OBJ)
const ERROR_MAP = getKey(tempVertical, ERROR_MAP_OBJ)

const formModel = ref({})
const validation = ref({})

const componentMap = {
  MoleculesSelectDropdown: resolveComponent('MoleculesSelectDropdown'),
  AtomsInput: resolveComponent('AtomsInput'),
  MoleculesPostcode: resolveComponent('MoleculesPostcode'),
  MoleculesOccupation: resolveComponent('MoleculesOccupation'),
  MoleculesStates: resolveComponent('MoleculesStates')
}

const VERTICAL_DATA = computed(() => {
  const form = WIDGET_DATA ?? {}
  const formData = form?.formData

  if (_isEmpty(formData)) {
    return {}
  }

  formData.forEach(item => {
    // not sure props.value doesn't exists
    delete item.props.value

    const key = item.props.name

    item.props.errorMessage = validation.value[key]?.errorMessage || ''
    item.props.isSuccess = validation.value[key]?.isSuccess || false
  })

  return form
})

handleModels()

const postcodeList = await (async () => {
  const response = await useAsyncData(() => $fetch(geodataSource))

  if (!_isEmpty(response?.error.value)) {
    return []
  }

  const list = response?.data?.value ?? []

  return list
})()

function handleModels () {
  if (_isEmpty(VERTICAL_DATA.value)) {
    return {}
  }

  formModel.value = GENERATE_FORM_MODEL_TEST({
    form: VERTICAL_DATA.value.formItemsArr
  })

  validation.value = GENERATE_FORM_MODEL_TEST({
    type: 'validation',
    form: VERTICAL_DATA.value.formItemsArr
  })
}

function handleValidateForm (_KEY) {
  const validate = (question, tempValue) => {
    const validationFunc = getKey(question, {
      default: value => !_isEmpty(value),
      age: value => validateAge(+value, [16, 120])
    })

    if (validationFunc) {
      return validationFunc(tempValue)
    }
  }

  if (_KEY) {
    const key = strToSnakeCase(_KEY)
    const currValue = formModel.value[key]
    const isValid = validate(key, currValue)

    validation.value[key] = {
      isSuccess: isValid,
      errorMessage: isValid
        ? ''
        : ERROR_MAP.default?.[key] ?? ''
    }
    
    return
  }

  for (const key in validation.value) {
    if (Object.hasOwnProperty.call(validation.value, key)) {
      const currValue = formModel.value?.[key]
      const isValid = validate(key, currValue)

      validation.value[key] = {
        isSuccess: isValid,
        errorMessage: isValid
          ? ''
          : ERROR_MAP.default?.[key] ?? ''
      }
    }
  }
}

function getPostCodeByState (state) {
  if (!state) {
    return ''
  }

  const postcode = postcodeList.find(item => item[1] === state)

  return !_isEmpty(postcode)
    ? postcode
    : []
}

async function handleSubmit () {
  await handleValidateForm()

  const hasError = (Object.values(validation.value)
    .map(item => item.isSuccess)
    .some(item => !item))

  if (hasError) {
    return
  }

  const model = formModel.value

  const map = getKey(props.vertical, {
    [VERTICAL.HEALTH_INSURANCE]: () => {
      const lifeStage = ['Couple', 'Family'].includes(model?.life_stage) ? 'couple' : 'single'

      const healthFund = model?.health_fund
      const hasHealthFund = healthFund === 'not insured' ? 'No' : 'Yes'
      const coverType = model?.cover_type

      const payload = {
        step1: {
          life_stage: model?.life_stage,
          postcode: model?.postcode
        },
        step2: {
          age: model?.age,
          age_secondary: (lifeStage === 'couple' && model.age) || '',
          health_fund: healthFund,
          has_health_fund: hasHealthFund,
          partner_has_health_fund: (lifeStage === 'couple' && hasHealthFund) || '',
          partner_health_fund: (lifeStage === 'couple' && healthFund) || ''
        },
        step3: {
          cover_type: coverType,
          extras_cover_list:
            ['Hospital', 'Combined'].includes(coverType)
              ? [PAGE_DATA.extrasCoverList[0].value]
              : [],
          hospital_cover_list:
            ['Extras', 'Combined'].includes(coverType)
              ? [PAGE_DATA.hospitalCoverList[0].value]
              : []
        }
      }

      healthInsuranceStore.form = JSON.parse(JSON.stringify(payload))

      $router.push(VERTICAL_DATA.value.redirect)
    },

    [VERTICAL.LIFE_INSURANCE]: () => {
      if (!props.isIncomeProtection) {
        const fullAddress = getPostCodeByState(model.state)

        const payload = {
          step1: {
            age: model?.age,
            annual_income: model?.cover_amount.replace(/[$,]/g, '')
          },
          step2: {
            smoker: model?.smoker,
            gender: model?.gender,
            occupation: model?.occupation,
            state: fullAddress[1]
          }
        }

        lifeInsuranceStore.form = JSON.parse(JSON.stringify(payload))

        $router.push(VERTICAL_DATA.value.redirect)
      } else {
        incomeProtectionStore.form = JSON.parse(JSON.stringify(model))

        $router.push(VERTICAL_DATA.value.redirect)
      }
    }
  })

  if (map && map?.()) {
    map()
  }
}
</script>
